// Cookie consent state + Google Consent Mode v2 helpers.
// Mirrors the direct-localStorage pattern used in AuthContext / BusinessContext.

const CONSENT_KEY = "cookie_consent";

export type ConsentCategory = "analytics" | "marketing";

// A category is absent until the visitor has been asked about it: `marketing` in choices stored
// before the category existed, `analytics` when only a host pixel prompted the banner. Absent always
// reads as denied and as undecided, never as inherited from the other category's answer.
export type ConsentChoice = {
    analytics?: boolean;
    marketing?: boolean;
    timestamp: string;
};

export function readConsent(): ConsentChoice | null {
    try {
        const raw = localStorage.getItem(CONSENT_KEY);
        return raw ? (JSON.parse(raw) as ConsentChoice) : null;
    } catch {
        return null;
    }
}

// Merges into the stored choice, so answering one category keeps the earlier answer to the other.
export function writeConsent(update: Partial<Record<ConsentCategory, boolean>>): ConsentChoice {
    const stored = readConsent();
    const choice: ConsentChoice = {timestamp: new Date().toISOString()};
    for (const category of ["analytics", "marketing"] as const) {
        const value = update[category] ?? stored?.[category];
        if (typeof value === "boolean") choice[category] = value;
    }
    try {
        localStorage.setItem(CONSENT_KEY, JSON.stringify(choice));
    } catch {
        // ignore write failures (e.g. storage disabled)
    }
    return choice;
}

export function hasDecided(category: ConsentCategory): boolean {
    const stored = readConsent();
    return typeof stored?.[category] === "boolean";
}

export function hasMarketingConsent(): boolean {
    return readConsent()?.marketing === true;
}

// ─── Change notifications ─────────────────────────────────────────────────────
// Tags that load on demand (the host Meta pixels) need to hear about a grant made after the page
// that wanted them has already rendered.

type ConsentListener = (choice: ConsentChoice) => void;
const consentListeners = new Set<ConsentListener>();

export function onConsentChange(listener: ConsentListener): () => void {
    consentListeners.add(listener);
    return () => {
        consentListeners.delete(listener);
    };
}

// Push a Consent Mode v2 update so GA reacts to the user's choice at runtime, then tell any
// on-demand tags.
export function applyConsent(choice: ConsentChoice) {
    const marketing = choice.marketing === true ? "granted" : "denied";
    window.gtag?.("consent", "update", {
        analytics_storage: choice.analytics === true ? "granted" : "denied",
        ad_storage: marketing,
        ad_user_data: marketing,
        ad_personalization: marketing,
    });
    consentListeners.forEach((listener) => listener(choice));
}

// ─── Marketing tag presence ───────────────────────────────────────────────────
// Host pixels come from the backend per event, not from build config, so the banner only learns
// that a marketing tag wants to load once a page asks for one.

let marketingTagRequested = false;
const presenceListeners = new Set<() => void>();

export function noteMarketingTagRequested(): void {
    if (marketingTagRequested) return;
    marketingTagRequested = true;
    presenceListeners.forEach((listener) => listener());
}

export function isMarketingTagRequested(): boolean {
    return marketingTagRequested;
}

export function subscribeMarketingTagRequested(listener: () => void): () => void {
    presenceListeners.add(listener);
    return () => {
        presenceListeners.delete(listener);
    };
}
